import { useEffect, useState, useRef } from "react";
import { contactUs } from "../../../api/ContactUs";
import { validateEmail } from "../../shared/Utils/ValidateEmail";
import { errorHandler } from "../../shared/Utils/ErrorHandler";
import { lineBreaks } from "../../shared/Utils/LineBreaks";
import { useLocation } from "react-router-dom";
import SecondaryButton from "../../shared/Buttons/SecondaryButton";
import { Turnstile } from "@marsidev/react-turnstile";

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    reason: "",
  });
  const [feError, setFEError] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState({ error: "" });
  const [ifFromEvents, setIfFromEvents] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);

  const location = useLocation();
  const { fromEvents } = location.state || {};

  useEffect(() => {
    if (fromEvents) {
      setIfFromEvents(true);
    }
  }, [fromEvents]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const contactUsCall = async (formData) => {
    try {
      const response = await contactUs(formData);
      setSuccess("Your message has been sent successfully!");
      setError({ error: "" });
    } catch (error) {
      let errorStr = errorHandler(error);
      const errorMessageWithBreaks = lineBreaks(errorStr);
      setError({ error: errorMessageWithBreaks });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault(); // Prevent the default form submission behavior

    // Validation for the name length
    if (formData.name.length > 25) {
      setFEError("Name must be under 25 characters.");
      setSuccess("");
      return; // Stop further execution if validation fails
    }

    // Validation for the email format
    if (!validateEmail(formData.email)) {
      setFEError("Please enter a valid email address.");
      setSuccess("");
      return; // Stop further execution if validation fails
    }

    // Validation for Turnstile captcha
    if (!turnstileToken) {
      setFEError("Please complete the captcha verification.");
      setSuccess("");
      return;
    }

    setFEError("");

    // If all validations pass, make the API call with the turnstile token
    contactUsCall({ ...formData, turnstile_token: turnstileToken });

    // Clear the form and reset Turnstile
    setTimeout(() => {
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        reason: "",
      });
      setTurnstileToken("");
      turnstileRef.current?.reset();
    }, 1000);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="sm:grow flex flex-col justify-center items-center w-full !my-0 !py-32">
      <h1 className="text-orange text-3xl">Contact Us</h1>
      <form
        className="max-w-3xl w-full mx-auto my-12 px-4"
        onSubmit={handleSubmit}
      >
        <div className="flex flex-col md:flex-row justify-between md:gap-6 gap-4 mb-4">
          <input
            type="text"
            name="name"
            className="border border-gray-400 rounded p-4 h-4 text-base"
            value={formData.name}
            onChange={handleChange}
            placeholder="Name"
          />
          <input
            type="text"
            name="email"
            className="border border-gray-400 rounded p-4 h-4 text-base"
            value={formData.email}
            onChange={handleChange}
            placeholder="Email"
          />
        </div>
        <input
          type="number"
          name="phone"
          className="border border-gray-400 rounded p-4 h-4 text-base mb-4"
          value={formData.phone}
          onChange={handleChange}
          placeholder="Phone Number"
        />
        <label className="block !w-full text-sm text-left">
          Reason for contacting us
        </label>
        <select
          name="reason"
          className="border border-gray-400 px-4 rounded h-12 text-base mb-4 w-full !text-gray-400"
          value={formData.reason}
          onChange={handleChange}
        >
          <option value="" disabled hidden className="!text-gray-200">
            Select a reason
          </option>
          <option value="Affiliate" className="text-black">
            Affiliate
          </option>
          <option value="Donation" className="text-black">
            Donation
          </option>
          <option value="Technical Issue" className="text-black">
            Technical Issue
          </option>
          <option value="Consulting" className="text-black">
            Consulting
          </option>
          <option value="Foster Interest" className="text-black">
            Foster Interest
          </option>
          <option value="Resources" className="text-black">
            Resources
          </option>
          <option value="Other" className="text-black">
            Other
          </option>
        </select>
        <textarea
          type="text"
          name="message"
          className="border border-gray-400 rounded p-4 h-48 w-full text-base mb-4"
          value={formData.message}
          onChange={handleChange}
          placeholder={ifFromEvents ? "Please describe your event." : "Message"}
        />
        <div className="mb-4">
          <Turnstile
            ref={turnstileRef}
            siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
            onSuccess={setTurnstileToken}
            onError={() => setTurnstileToken("")}
            onExpire={() => setTurnstileToken("")}
          />
        </div>
        <SecondaryButton type="submit" className="!w-1/4">
          Send
        </SecondaryButton>
        {feError && <div className="text-error">{feError}</div>}
        {error.error && <div className="text-error">{error.error}</div>}
        {success && <div className="text-success">{success}</div>}
      </form>
    </div>
  );
}
